import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { Badge, Button, Card, Input, Select, Textarea } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import { useTaxCalculation, type CalculateTaxesInput } from '../fiscal/tax-calculate/useTaxCalculate';

/* ── Contrato ──────────────────────────────────────────────────────────────
 * `CreateSalesOrderDto` aceita, por item, `discount` + `discountType`
 * (PERCENTAGE | FIXED) e `taxValues: Record<string, number>`, e no pedido um
 * `discount { type, value }`. A versão anterior mandava `discount: 0`,
 * `discountType: 'FIXED'` e `taxValues: {}` fixos — três campos do contrato
 * usados como zero.
 */

type DiscountType = 'PERCENTAGE' | 'FIXED';

/** Espelha `ProductResponseDto` no que esta tela usa. */
interface Produto {
  id: string;
  name: string;
  sku: string;
  unit: string;
  price: number;
  ncmCode?: string;
  cfop?: string;
  status: string;
}

/** Espelha `CustomerResponseDto` no que esta tela usa. */
interface Cliente {
  id: string;
  name: string;
  documentType?: string;
  documentNumber?: string;
  isBlocked: boolean;
}

interface LinhaDoPedido {
  key: string;
  productId: string;
  description: string;
  ncmCode: string;
  quantity: string;
  unitPrice: string;
  discount: string;
  discountType: DiscountType;
}

interface OrderDetailItem {
  productId: string;
  description: string;
  quantity: number;
  unitValue: number;
  total: number;
}

interface OrderDetail {
  id: string;
  customerId: string;
  customerName: string | null;
  status: string;
  items: OrderDetailItem[];
  grandTotal: number;
  conditions: string;
  paymentTerms: string;
  fiscalDocumentId: string | null;
  createdAt: string;
}

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE',
  'TO',
] as const;

/** Condições de pagamento aceitas como texto livre por `paymentTerms`. */
const CONDICOES = [
  'À vista',
  '30 dias (boleto)',
  '15 dias (boleto)',
  'PIX antecipado',
  '30/60 dias',
  '30/60/90 dias',
] as const;

let proximaChave = 0;
function novaLinha(): LinhaDoPedido {
  proximaChave += 1;
  return {
    key: `linha-${proximaChave}`,
    productId: '',
    description: '',
    ncmCode: '',
    quantity: '1',
    unitPrice: '',
    discount: '0',
    discountType: 'PERCENTAGE',
  };
}

function numero(valor: string): number {
  const n = Number(valor.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/** Bruto da linha, antes do desconto. */
function brutoDaLinha(linha: LinhaDoPedido): number {
  return numero(linha.quantity) * numero(linha.unitPrice);
}

/** Desconto em reais, seja ele percentual ou valor. */
function descontoDaLinha(linha: LinhaDoPedido): number {
  const bruto = brutoDaLinha(linha);
  const d = numero(linha.discount);
  if (linha.discountType === 'PERCENTAGE') return (bruto * Math.min(d, 100)) / 100;
  return Math.min(d, bruto);
}

function liquidoDaLinha(linha: LinhaDoPedido): number {
  return Math.max(brutoDaLinha(linha) - descontoDaLinha(linha), 0);
}

export function SalesFormPage(): JSX.Element {
  const navigate = useNavigate();
  /* O mesmo componente atende `/sales/new` e a rota de edicao: pedir os
     parametros com `from` fixo estoura na rota que nao os tem. */
  const params = useParams({ strict: false }) as { saleId?: string };
  const saleId = params.saleId;
  const isEditing = !!saleId;

  const [customerId, setCustomerId] = useState('');
  const [buscaCliente, setBuscaCliente] = useState('');
  const [paymentTerms, setPaymentTerms] = useState<string>(CONDICOES[0]);
  const [conditions, setConditions] = useState('');
  const [ufOrigem, setUfOrigem] = useState('SP');
  const [ufDestino, setUfDestino] = useState('SP');
  const [linhas, setLinhas] = useState<LinhaDoPedido[]>(() => [novaLinha()]);
  const [descontoPedido, setDescontoPedido] = useState('0');
  const [tipoDescontoPedido, setTipoDescontoPedido] = useState<DiscountType>('FIXED');
  const [erros, setErros] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [calcularTributos, setCalcularTributos] = useState(false);

  /* Modo detalhe: não há `PUT /orders/:id`. Carrega o pedido real e mostra em
     leitura — faturar e cancelar seguem pela lista. */
  const { data: orderDetail, isLoading: carregandoPedido, error: erroPedido } = useQuery({
    queryKey: ['sales', 'orders', saleId ?? 'new'],
    queryFn: async () => apiClient.get<OrderDetail>(`/orders/${saleId}`),
    enabled: isEditing,
  });

  const clientesQ = useQuery({
    queryKey: ['customers', 'pedido'],
    queryFn: async () => apiClient.get<Cliente[]>('/customers'),
    staleTime: 5 * 60 * 1000,
    enabled: !isEditing,
  });

  const produtosQ = useQuery({
    queryKey: ['products', 'pedido'],
    queryFn: async () => apiClient.get<Produto[]>('/products'),
    staleTime: 5 * 60 * 1000,
    enabled: !isEditing,
  });

  const produtos = useMemo(() => produtosQ.data ?? [], [produtosQ.data]);
  const produtoPorId = useMemo(() => {
    const mapa = new Map<string, Produto>();
    for (const p of produtos) mapa.set(p.id, p);
    return mapa;
  }, [produtos]);

  const clientes = useMemo(() => clientesQ.data ?? [], [clientesQ.data]);
  const clienteSelecionado = clientes.find((c) => c.id === customerId);

  const sugestoes = useMemo(() => {
    const termo = buscaCliente.trim().toLowerCase();
    if (termo.length < 2 || clienteSelecionado) return [];
    return clientes.filter((c) => c.name.toLowerCase().includes(termo)).slice(0, 8);
  }, [buscaCliente, clientes, clienteSelecionado]);

  /* ── Tributos pelo motor fiscal ──────────────────────────────────────────
   * O item do pedido tem `taxValues` no contrato e a tela antiga mandava `{}`.
   * `POST /fiscal/calculate` é o mesmo motor que o Simulador de Impacto usa:
   * regras e alíquotas do cadastro, na data de hoje. Nada de alíquota fixa no
   * cliente. */
  const itensParaCalculo = useMemo(
    () =>
      linhas
        .filter((l) => l.ncmCode.replace(/\D/g, '').length >= 4 && brutoDaLinha(l) > 0)
        .map((l) => ({
          ncmCode: l.ncmCode.replace(/\D/g, ''),
          quantity: numero(l.quantity),
          /* O motor calcula sobre quantidade × valor unitário; para o desconto
             entrar na base, o unitário enviado é o líquido da linha. */
          unitValue: liquidoDaLinha(l) / Math.max(numero(l.quantity), 1),
        })),
    [linhas],
  );

  const pedidoDeCalculo: CalculateTaxesInput | null = useMemo(() => {
    if (!calcularTributos || itensParaCalculo.length === 0) return null;
    return {
      operationType: 'VENDA',
      originUf: ufOrigem,
      destinationUf: ufDestino,
      emitterRegime: 'LUCRO_PRESUMIDO',
      destinationRegime: 'LUCRO_PRESUMIDO',
      items: itensParaCalculo,
    };
  }, [calcularTributos, itensParaCalculo, ufOrigem, ufDestino]);

  const tributosQ = useTaxCalculation(pedidoDeCalculo);

  /** Tributo por NCM, para casar com a linha na tela e no `taxValues`. */
  const tributoPorNcm = useMemo(() => {
    const mapa = new Map<string, Record<string, number>>();
    for (const item of tributosQ.data?.itemDetails ?? []) {
      const valores: Record<string, number> = {};
      for (const [nome, linha] of Object.entries(item.taxes)) valores[nome] = linha.value;
      mapa.set(item.ncm, valores);
    }
    return mapa;
  }, [tributosQ.data]);

  function tributoDaLinha(linha: LinhaDoPedido): Record<string, number> {
    return tributoPorNcm.get(linha.ncmCode.replace(/\D/g, '')) ?? {};
  }

  function totalTributoDaLinha(linha: LinhaDoPedido): number {
    return Object.values(tributoDaLinha(linha)).reduce((s, v) => s + v, 0);
  }

  /* ── Totais ── */
  const subtotal = linhas.reduce((s, l) => s + brutoDaLinha(l), 0);
  const descontoItens = linhas.reduce((s, l) => s + descontoDaLinha(l), 0);
  const liquidoItens = subtotal - descontoItens;
  const descontoCabecalho =
    tipoDescontoPedido === 'PERCENTAGE'
      ? (liquidoItens * Math.min(numero(descontoPedido), 100)) / 100
      : Math.min(numero(descontoPedido), liquidoItens);
  const totalTributos = tributosQ.data?.totals.totalTax ?? 0;
  const totalPedido = Math.max(liquidoItens - descontoCabecalho, 0);

  const atualizaLinha = useCallback(
    (key: string, campo: keyof Omit<LinhaDoPedido, 'key'>, valor: string) => {
      setLinhas((atual) =>
        atual.map((l) => (l.key === key ? { ...l, [campo]: valor } : l)),
      );
    },
    [],
  );

  /** Escolher o produto traz descrição, NCM e preço de tabela. */
  const escolheProduto = useCallback(
    (key: string, productId: string) => {
      const produto = produtoPorId.get(productId);
      setLinhas((atual) =>
        atual.map((l) =>
          l.key === key
            ? {
                ...l,
                productId,
                description: produto?.name ?? l.description,
                ncmCode: produto?.ncmCode ?? '',
                unitPrice: produto ? String(produto.price) : l.unitPrice,
              }
            : l,
        ),
      );
    },
    [produtoPorId],
  );

  useEffect(() => {
    if (!orderDetail) return;
    setCustomerId(orderDetail.customerId);
    setBuscaCliente(orderDetail.customerName ?? orderDetail.customerId);
    setConditions(orderDetail.conditions ?? '');
    setPaymentTerms(orderDetail.paymentTerms || CONDICOES[0]);
  }, [orderDetail]);

  const valida = (): boolean => {
    const novos: string[] = [];
    if (!customerId) novos.push('Selecione o cliente.');
    if (clienteSelecionado?.isBlocked) novos.push('Cliente bloqueado: o pedido não pode ser emitido.');
    const validas = linhas.filter((l) => l.productId && brutoDaLinha(l) > 0);
    if (validas.length === 0) novos.push('Adicione ao menos um item com produto, quantidade e valor.');
    if (linhas.some((l) => l.productId && !l.description.trim())) {
      novos.push('Informe a descrição de todos os itens.');
    }
    if (linhas.some((l) => l.productId && numero(l.quantity) <= 0)) {
      novos.push('A quantidade precisa ser maior que zero.');
    }
    setErros(novos);
    return novos.length === 0;
  };

  const salvar = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valida()) return;
    setEnviando(true);
    try {
      const payload = {
        customerId,
        ...(conditions.trim() ? { conditions: conditions.trim() } : {}),
        ...(paymentTerms ? { paymentTerms } : {}),
        items: linhas
          .filter((l) => l.productId && brutoDaLinha(l) > 0)
          .map((l) => ({
            productId: l.productId,
            description: l.description.trim(),
            quantity: numero(l.quantity),
            unitValue: numero(l.unitPrice),
            /* Desconto e tipo do contrato, não zero fixo. */
            discount: numero(l.discount),
            discountType: l.discountType,
            /* Tributos apurados pelo motor fiscal, quando calculados. */
            taxValues: tributoDaLinha(l),
            type: 'PRODUCT',
          })),
        ...(numero(descontoPedido) > 0
          ? { discount: { type: tipoDescontoPedido, value: numero(descontoPedido) } }
          : {}),
      };

      await apiClient.post('/orders', payload);
      navigate({ to: '/sales' }).catch(console.error);
    } catch (error) {
      setErros([(error as Error).message]);
    } finally {
      setEnviando(false);
    }
  };

  /* ── Modo detalhe ── */
  if (isEditing) {
    return (
      <div className="sf-page">
        <div className="sf-header">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">
              Pedido de venda
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
              Itens, cliente e totais do pedido.
            </p>
          </div>
          {orderDetail ? <Badge variant="neutral">{orderDetail.status}</Badge> : null}
        </div>

        <Card>
          <div className="sf-card">
            {carregandoPedido ? (
              <p className="sf-muted">Carregando pedido…</p>
            ) : erroPedido ? (
              <>
                <p className="sf-error" role="alert">
                  {(erroPedido as Error).message ?? 'Pedido não encontrado'}
                </p>
                <Button type="button" variant="outline" onClick={() => navigate({ to: '/sales' })}>
                  Voltar para a lista
                </Button>
              </>
            ) : orderDetail ? (
              <>
                <p className="sf-hint" role="note">
                  Pedido gravado. Não há rota de alteração: faturar e cancelar são
                  ações da lista de pedidos.
                </p>
                <div className="sf-detail">
                  <div>
                    <span className="sf-label">Cliente</span>
                    <p className="sf-value">
                      {orderDetail.customerName ?? <code>{orderDetail.customerId}</code>}
                    </p>
                  </div>
                  <div>
                    <span className="sf-label">Emissão</span>
                    <p className="sf-value">{formatDate(orderDetail.createdAt)}</p>
                  </div>
                  <div>
                    <span className="sf-label">Condição</span>
                    <p className="sf-value">{orderDetail.paymentTerms || '—'}</p>
                  </div>
                  <div>
                    <span className="sf-label">Documento fiscal</span>
                    <p className="sf-value">
                      {orderDetail.fiscalDocumentId ? (
                        <code>{orderDetail.fiscalDocumentId}</code>
                      ) : (
                        'não faturado'
                      )}
                    </p>
                  </div>
                </div>
                <table className="sf-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th className="sf-num">Qtd.</th>
                      <th className="sf-num">Valor unit.</th>
                      <th className="sf-num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderDetail.items.map((item, i) => (
                      <tr key={`${item.productId}-${i}`}>
                        <td>{item.description}</td>
                        <td className="sf-num">{item.quantity}</td>
                        <td className="sf-num">{formatCurrency(item.unitValue)}</td>
                        <td className="sf-num">{formatCurrency(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>Total do pedido</td>
                      <td className="sf-num">{formatCurrency(orderDetail.grandTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </>
            ) : null}
          </div>
        </Card>
      </div>
    );
  }

  /* ── Novo pedido ── */
  return (
    <form className="sf-page" onSubmit={(e) => void salvar(e)}>
      <div className="sf-header">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Novo pedido de venda
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Cliente, itens e condições. Os tributos saem do motor fiscal, não de
            alíquota fixa.
          </p>
        </div>
        <div className="sf-actions">
          <Button type="button" variant="outline" onClick={() => navigate({ to: '/sales' })}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={enviando}>
            Salvar pedido
          </Button>
        </div>
      </div>

      {erros.length > 0 && (
        <Card>
          <div className="sf-card" role="alert">
            <ul className="sf-errors">
              {erros.map((erro) => (
                <li key={erro}>{erro}</li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      {/* ── Cliente e condições ── */}
      <Card>
        <div className="sf-card">
          <h3 className="sf-section">Cliente e condições</h3>
          <div className="sf-grid">
            <div className="sf-lookup">
              <Input
                label="Cliente"
                placeholder="Digite o nome do cliente"
                value={buscaCliente}
                onChange={(e) => {
                  setBuscaCliente(e.target.value);
                  setCustomerId('');
                }}
              />
              {/* Busca por nome: a versão anterior pedia o UUID do cliente
                  colado num campo de texto. */}
              {sugestoes.length > 0 && (
                <ul className="sf-suggestions">
                  {sugestoes.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setCustomerId(c.id);
                          setBuscaCliente(c.name);
                          if (!ufDestino) setUfDestino(ufOrigem);
                        }}
                      >
                        <span>{c.name}</span>
                        <small>
                          {c.documentNumber ? `${c.documentType ?? 'Doc'} ${c.documentNumber}` : 'sem documento'}
                          {c.isBlocked ? ' · bloqueado' : ''}
                        </small>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {clienteSelecionado && (
                <p className="sf-hint">
                  {clienteSelecionado.documentNumber
                    ? `${clienteSelecionado.documentType ?? 'Doc'} ${clienteSelecionado.documentNumber}`
                    : 'sem documento cadastrado'}
                  {clienteSelecionado.isBlocked ? ' · CLIENTE BLOQUEADO' : ''}
                </p>
              )}
            </div>
            <Select
              label="Condição de pagamento"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
            >
              {CONDICOES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <Select label="UF de origem" value={ufOrigem} onChange={(e) => setUfOrigem(e.target.value)}>
              {UFS.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </Select>
            <Select
              label="UF de destino"
              value={ufDestino}
              onChange={(e) => setUfDestino(e.target.value)}
            >
              {UFS.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {/* ── Itens ── */}
      <Card>
        <div className="sf-card">
          <div className="sf-section-head">
            <h3 className="sf-section">Itens</h3>
            <Button type="button" variant="secondary" size="sm" onClick={() => setLinhas((a) => [...a, novaLinha()])}>
              Adicionar item
            </Button>
          </div>

          <div className="sf-table-wrap">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>NCM</th>
                  <th className="sf-num">Qtd.</th>
                  <th className="sf-num">Valor unit.</th>
                  <th className="sf-num">Desconto</th>
                  <th className="sf-num">Tributos</th>
                  <th className="sf-num">Total</th>
                  <th>
                    <span className="sf-sr">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((linha) => {
                  const tributos = tributoDaLinha(linha);
                  const nomes = Object.keys(tributos);
                  return (
                    <tr key={linha.key}>
                      <td className="sf-cell--produto">
                        <Select
                          aria-label="Produto"
                          value={linha.productId}
                          onChange={(e) => escolheProduto(linha.key, e.target.value)}
                        >
                          <option value="">Selecione…</option>
                          {produtos.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.sku ? `${p.sku} — ` : ''}
                              {p.name}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td className="sf-mono">
                        <Input
                          aria-label="NCM"
                          className="sf-ncm"
                          value={linha.ncmCode}
                          placeholder="—"
                          onChange={(e) =>
                            atualizaLinha(linha.key, 'ncmCode', e.target.value.replace(/\D/g, '').slice(0, 8))
                          }
                        />
                      </td>
                      <td className="sf-num">
                        <Input
                          aria-label="Quantidade"
                          className="sf-qty"
                          type="number"
                          min={0}
                          step="0.001"
                          value={linha.quantity}
                          onChange={(e) => atualizaLinha(linha.key, 'quantity', e.target.value)}
                        />
                      </td>
                      <td className="sf-num">
                        <Input
                          aria-label="Valor unitário"
                          className="sf-qty"
                          type="number"
                          min={0}
                          step="0.01"
                          placeholder="0,00"
                          value={linha.unitPrice}
                          onChange={(e) => atualizaLinha(linha.key, 'unitPrice', e.target.value)}
                        />
                      </td>
                      <td className="sf-num">
                        <div className="sf-desconto">
                          <Input
                            aria-label="Desconto"
                            className="sf-qty"
                            type="number"
                            min={0}
                            step="0.01"
                            value={linha.discount}
                            onChange={(e) => atualizaLinha(linha.key, 'discount', e.target.value)}
                          />
                          <Select
                            aria-label="Tipo de desconto"
                            value={linha.discountType}
                            onChange={(e) => atualizaLinha(linha.key, 'discountType', e.target.value)}
                          >
                            <option value="PERCENTAGE">%</option>
                            <option value="FIXED">R$</option>
                          </Select>
                        </div>
                        {descontoDaLinha(linha) > 0 && (
                          <small className="sf-sub">− {formatCurrency(descontoDaLinha(linha))}</small>
                        )}
                      </td>
                      <td className="sf-num">
                        {nomes.length === 0 ? (
                          <span className="sf-muted">—</span>
                        ) : (
                          <>
                            {formatCurrency(totalTributoDaLinha(linha))}
                            <small className="sf-sub" title={nomes.join(', ')}>
                              {nomes.slice(0, 3).join(' · ')}
                              {nomes.length > 3 ? ` +${nomes.length - 3}` : ''}
                            </small>
                          </>
                        )}
                      </td>
                      <td className="sf-num">{formatCurrency(liquidoDaLinha(linha))}</td>
                      <td>
                        <button
                          type="button"
                          className="sf-icon"
                          onClick={() => setLinhas((a) => (a.length === 1 ? a : a.filter((l) => l.key !== linha.key)))}
                          disabled={linhas.length === 1}
                          aria-label="Remover item"
                          title="Remover item"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Tributos pelo motor ── */}
          <div className="sf-tributos">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={tributosQ.isFetching}
              disabled={itensParaCalculo.length === 0}
              onClick={() => setCalcularTributos(true)}
            >
              {calcularTributos ? 'Recalcular tributos' : 'Calcular tributos'}
            </Button>
            <span className="sf-hint">
              {itensParaCalculo.length === 0
                ? 'Informe produto, NCM e valor para o motor fiscal ter o que calcular.'
                : tributosQ.data
                  ? tributosQ.data.ruleApplied
                    ? `Regra aplicada: ${tributosQ.data.ruleApplied.name} · CFOP ${tributosQ.data.cfopResolved.code}`
                    : 'Nenhuma regra do cadastro casou com esta operação — os itens saem sem tributo.'
                  : 'O cálculo usa as regras e alíquotas do cadastro, na data de hoje.'}
            </span>
          </div>

          {(tributosQ.data?.errors.length ?? 0) > 0 && (
            <ul className="sf-errors sf-errors--aviso">
              {tributosQ.data?.errors.map((erro, i) => (
                <li key={i}>{erro}</li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* ── Resumo ── */}
      <Card>
        <div className="sf-card">
          <h3 className="sf-section">Resumo financeiro</h3>
          <div className="sf-resumo">
            <div className="sf-resumo__linhas">
              <div>
                <span>Subtotal dos itens</span>
                <strong>{formatCurrency(subtotal)}</strong>
              </div>
              <div>
                <span>Desconto nos itens</span>
                <strong>{descontoItens > 0 ? `− ${formatCurrency(descontoItens)}` : '—'}</strong>
              </div>
              <div>
                <span>Desconto do pedido</span>
                <strong>{descontoCabecalho > 0 ? `− ${formatCurrency(descontoCabecalho)}` : '—'}</strong>
              </div>
              <div>
                <span>Tributos apurados</span>
                <strong>{calcularTributos ? formatCurrency(totalTributos) : 'não calculado'}</strong>
              </div>
              <div className="sf-resumo__total">
                <span>Total do pedido</span>
                <strong>{formatCurrency(totalPedido)}</strong>
              </div>
            </div>

            <div className="sf-resumo__desconto">
              <span className="sf-label">Desconto no pedido</span>
              <div className="sf-desconto">
                <Input
                  aria-label="Desconto do pedido"
                  className="sf-qty"
                  type="number"
                  min={0}
                  step="0.01"
                  value={descontoPedido}
                  onChange={(e) => setDescontoPedido(e.target.value)}
                />
                <Select
                  aria-label="Tipo de desconto do pedido"
                  value={tipoDescontoPedido}
                  onChange={(e) => setTipoDescontoPedido(e.target.value as DiscountType)}
                >
                  <option value="FIXED">R$</option>
                  <option value="PERCENTAGE">%</option>
                </Select>
              </div>
              {/* `discount` do pedido é campo de `CreateSalesOrderDto` e não era
                  enviado: o desconto de cabeçalho não existia na tela. */}
              <Textarea
                label="Observações (condições do pedido)"
                value={conditions}
                onChange={(e) => setConditions(e.target.value)}
                rows={3}
                placeholder="Prazo de entrega, frete, particularidades…"
              />
            </div>
          </div>

          <p className="sf-hint">
            <strong>Emitir a nota não é ação desta tela.</strong>{' '}
            <code>PATCH /orders/:id/invoice</code> <em>vincula</em> um documento
            fiscal já emitido ao pedido — não emite. A emissão é em Documentos
            Fiscais, e o vínculo é feito depois.
          </p>
        </div>
      </Card>

      <style>{`
        .sf-page { padding: 24px; display: flex; flex-direction: column; gap: 16px; }
        .sf-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; margin-bottom: 8px; }
        .sf-actions { display: flex; gap: 8px; }
        .sf-card { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
        .sf-section { font-family: Inter, sans-serif; font-size: 20px; line-height: 28px; font-weight: 600; color: #191c1e; margin: 0; }
        .sf-section-head { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
        .sf-hint { font-size: 12px; line-height: 18px; color: #43474e; margin: 0; }
        .sf-muted { font-size: 12px; color: #74777f; }
        .sf-error { font-size: 12px; color: #ba1a1a; margin: 0; }
        .sf-label { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em; color: #43474e; }
        .sf-value { margin: 2px 0 0; font-size: 12px; color: #191c1e; font-weight: 600; }
        .sf-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
        .sf-grid { display: grid; grid-template-columns: minmax(240px, 2fr) minmax(180px, 1fr) 100px 100px; gap: 12px; align-items: start; }
        @media (max-width: 900px) { .sf-grid { grid-template-columns: 1fr 1fr; } }
        .sf-detail { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
        .sf-lookup { position: relative; }
        .sf-suggestions { position: absolute; z-index: 20; top: 100%; left: 0; right: 0; margin: 4px 0 0; padding: 0; list-style: none; background: #ffffff; border: 1px solid #c4c6cf; border-radius: 0.25rem; box-shadow: 0 4px 12px rgba(0,0,0,0.08); max-height: 240px; overflow-y: auto; }
        .sf-suggestions button { display: flex; flex-direction: column; gap: 2px; width: 100%; text-align: left; background: none; border: none; padding: 8px 12px; cursor: pointer; font-size: 12px; color: #191c1e; }
        .sf-suggestions button:hover { background: #f2f4f6; }
        .sf-suggestions small { color: #74777f; }
        .sf-errors { margin: 0; padding-left: 18px; font-size: 12px; line-height: 18px; color: #ba1a1a; }
        .sf-errors--aviso { color: #633f0f; }
        .sf-table-wrap { overflow-x: auto; }
        .sf-table { width: 100%; border-collapse: collapse; }
        .sf-table th { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; font-weight: 500; letter-spacing: 0.02em; text-transform: uppercase; color: #43474e; text-align: left; padding: 8px 8px; border-bottom: 1px solid #c4c6cf; white-space: nowrap; }
        .sf-table td { padding: 6px 8px; border-bottom: 1px solid #eceef0; font-size: 12px; color: #191c1e; vertical-align: top; }
        .sf-table tfoot td { font-weight: 600; border-top: 1px solid #c4c6cf; border-bottom: none; }
        .sf-cell--produto { min-width: 220px; }
        .sf-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .sf-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .sf-sub { display: block; color: #74777f; font-size: 12px; }
        .sf-qty { width: 90px; text-align: right; }
        .sf-ncm { width: 110px; }
        .sf-desconto { display: flex; gap: 4px; align-items: center; justify-content: flex-end; }
        .sf-desconto > *:last-child { width: 64px; }
        .sf-tributos { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 8px; border-radius: 0.25rem; background: #f2f4f6; }
        .sf-resumo { display: grid; grid-template-columns: minmax(260px, 1fr) minmax(260px, 1fr); gap: 24px; }
        @media (max-width: 900px) { .sf-resumo { grid-template-columns: 1fr; } }
        .sf-resumo__linhas { display: flex; flex-direction: column; gap: 6px; }
        .sf-resumo__linhas > div { display: flex; justify-content: space-between; gap: 16px; font-size: 12px; color: #43474e; }
        .sf-resumo__linhas strong { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; color: #191c1e; }
        .sf-resumo__total { border-top: 1px solid #c4c6cf; padding-top: 8px; margin-top: 4px; }
        .sf-resumo__total span { font-weight: 600; color: #191c1e; }
        .sf-resumo__total strong { font-family: Inter, sans-serif; font-size: 20px; line-height: 28px; font-weight: 600; }
        .sf-resumo__desconto { display: flex; flex-direction: column; gap: 8px; }
        .sf-icon { background: none; border: none; cursor: pointer; color: #43474e; font-size: 16px; line-height: 1; padding: 4px 8px; border-radius: 0.25rem; }
        .sf-icon:hover:not(:disabled) { background: #e0e3e5; color: #191c1e; }
        .sf-icon:disabled { color: #c4c6cf; cursor: not-allowed; }
      `}</style>
    </form>
  );
}
